import { KING_VALUE } from 'constants/cards';
import { SolitaireGame } from 'game/SolitaireGame';
import { HintItem, HintType } from './hintGroup';
import { Card } from 'components/Card';
import { Store } from 'game/store/gameStore';

// ======== The priority of the available moves shows in order: =========
// 1 .Moving cards from the Tableau or Waste pile area to the Foundation.
// 2. Moving a card from one column to another to uncover a face-down card in the Tableau area.
// 3. Moving Kings. If there are empty spaces on tableau (places for Kings) the hint will suggest moving a King to these spaces.
// 4. Moving a card from the Waste Pile to the Tableau.
// 5. Moving cards between columns to free up a space for a King.
// 6. Move a card from the foundation to the tableau, to flip a card and reveal a face-down card on the tableau in the next move.
// 7. Uncovering a card in the Stockpile area (or Flip all cards in the Waste Pile back (click on the empty Stockpile). Analyze if any of the cards from the stockpile can be moved to the foundation or tableau. If we can’t move any of the cards, skip this step.
// 8. Move a card or a column of cards on the tableau to set up a move to the foundation in the next step.
// 9. If we don’t have any available hint, we show popup:

export function findKlondikeHint(game: SolitaireGame, lastPriorityCheck: number, hintCards: string[]) {
    let hints = [] as HintItem[];

    for (let i = lastPriorityCheck; i < 9; i++) {
        if (i === 0) continue;
        switch (i) {
            case 1:
                hints = checkHint1(game, hintCards);
                break;
            case 2:
                hints = checkHint2(game, hintCards);
                break;
            case 3:
                hints = checkHint3(game, hintCards);
                break;
            case 4:
                hints = checkHint4(game, hintCards);
                break;
            case 5:
                hints = checkHint5(game, hintCards);
                break;
            case 6:
                hints = checkHint6(game, hintCards);
                break;
            case 7:
                hints = checkHint7(game, hintCards);
                break;
            case 8:
                hints = checkHint8(game, hintCards);
                if (!hints.length && lastPriorityCheck > 0) {
                    hints = [new HintItem([], game.columns[0], HintType.RESET, 8)];
                }
                break;
        }
        if (hints.length) return hints;
    }
    return hints;
}
function checkHint1(game: SolitaireGame, hintCards: string[]) {
    // ==== CHECK 1: Moving cards from the Tableau or Waste pile area to the Foundation. ====
    // from left to right columns then waste pile
    const lastColumnCards = game.columns.map((c) => c.lastCard);
    const lastWastePileCards = game.wastePile?.lastCard;

    for (const card of [...lastColumnCards, lastWastePileCards]) {
        if (!card) continue;
        if (hintCards.includes(card._id)) continue;
        const validFoundation = game.foundations.find((foundation) => foundation.canDropCard([card]));
        if (validFoundation) {
            return [new HintItem([card], validFoundation, HintType.MOVE, 1)];
        }
    }
    return [];
}
function checkHint2(game: SolitaireGame, hintCards: string[]) {
    // ==== CHECK 2: Moving a card from one column to another to uncover a face-down card in the Tableau area. ====
    const activeColumnsMoveGroups = game.columns.map((c) => c.getLastMoveGroup());

    for (let col = 0; col < game.columns.length; col++) {
        const column = game.columns[col];
        if (column.hasHiddenCards) {
            const moveGroup = activeColumnsMoveGroups[col];
            if (!moveGroup.length) continue; // should always be true since cards are hidden above
            if (hintCards.includes(moveGroup[0]._id)) continue;

            const validColumn = game.columns.find((c, i) => {
                return i !== col && c.canDropCards(moveGroup);
            });
            if (validColumn) {
                return [new HintItem(moveGroup, validColumn, HintType.MOVE, 2)];
            }
        }
    }
    return [];
}
function checkHint3(game: SolitaireGame, hintCards: string[]) {
    // ==== CHECK 3: Moving Kings. If there are empty spaces on tableau (places for Kings) the hint will suggest moving a King to these spaces. ====
    const activeColumnsMoveGroups = game.columns.map((c) => c.getLastMoveGroup());
    const lastWastePileCards = game.wastePile?.lastCard;
    const emptyColumn = game.columns.find((c) => c.isEmpty);
    if (emptyColumn) {
        for (let col = 0; col < game.columns.length; col++) {
            const column = game.columns[col];
            const moveGroup = activeColumnsMoveGroups[col];
            if (!moveGroup[0]) continue;
            if (hintCards.includes(moveGroup[0]._id)) continue;
            if (moveGroup[0].value !== KING_VALUE) continue;
            if (column.cards[0].value === KING_VALUE) continue;

            return [new HintItem(moveGroup, emptyColumn, HintType.MOVE, 3)];
        }

        if (lastWastePileCards && lastWastePileCards.value === KING_VALUE) {
            return [new HintItem([lastWastePileCards], emptyColumn, HintType.MOVE, 3)];
        }
    }
    return [];
}
function checkHint4(game: SolitaireGame, hintCards: string[]) {
    // ==== CHECK 4: Moving a card from the Waste Pile to the Tableau. ====
    const lastWastePileCard = game.wastePile?.lastCard;
    if (lastWastePileCard) {
        if (hintCards.includes(lastWastePileCard._id)) return [];

        for (let col = 0; col < game.columns.length; col++) {
            const column = game.columns[col];
            if (column.canDropCards([lastWastePileCard])) {
                return [new HintItem([lastWastePileCard], column, HintType.MOVE, 4)];
            }
        }
    }
    return [];
}
function checkHint5(game: SolitaireGame, lastCard: string[]) {
    // CHECK 5: Moving cards between columns to free up a space for a King.
    const activeColumnsMoveGroups = game.columns.map((c) => c.getLastMoveGroup());

    for (let col = 0; col < game.columns.length; col++) {
        const moveGroup = activeColumnsMoveGroups[col];
        if (!moveGroup.length) continue;
        if (moveGroup[0].value === KING_VALUE) continue;
        if (lastCard.includes(moveGroup[0]._id)) continue;
        const validColumn = game.columns.find((c, i) => {
            return i !== col && c.canDropCards(moveGroup);
        });
        if (validColumn) {
            return [new HintItem(moveGroup, validColumn, HintType.MOVE, 5)];
        }
    }
    return [];
}
function checkHint6(game: SolitaireGame, lastCard: string[]) {
    // CHECK 6: Move a card from the foundation to the tableau, to flip a card and reveal a face-down card on the tableau in the next move.
    const activeColumnsMoveGroups = game.columns.map((c) => c.getLastMoveGroup());

    for (let col = 0; col < game.columns.length; col++) {
        const hiddenCardsColumn = game.columns[col];
        if (hiddenCardsColumn.hasHiddenCards) {
            const moveGroup = activeColumnsMoveGroups[col];
            if (lastCard.includes(moveGroup[0]._id)) continue;
            //we have a column with only one card covering others
            //check if foundation has a card with higher value in opposite color
            const validFoundation = game.foundations.filter((foundation) => {
                return foundation.lastCard && foundation.lastCard.canDropInColumn(moveGroup[0]);
            });
            //check if valid foundation card can be moved to any column
            if (validFoundation.length) {
                for (const foundation of validFoundation) {
                    if (foundation.lastCard) {
                        const moveCard = foundation.lastCard;
                        const validColumn = game.columns.find((c) => c.canDropCards([moveCard]));
                        if (validColumn) {
                            // make 2 moves animation
                            return [
                                new HintItem([foundation.lastCard], validColumn, HintType.MOVE, 6),
                                new HintItem(hiddenCardsColumn.getLastMoveGroup(), validColumn, HintType.MOVE, 6),
                            ];
                        }
                    }
                }
            }
        }
    }
    return [];
}
function checkHint7(game: SolitaireGame, lastCard: string[]) {
    // CHECK 7: Uncovering a card in the Stockpile area (or Flip all cards in the Waste Pile back (click on the empty Stockpile). Analyze if any of the cards from the stockpile can be moved to the foundation or tableau. If we can’t move any of the cards, skip this step.

    if (game.stockPile && game.wastePile) {
        //check waste pile also here ?

        for (let i = Store.dealCount - 1; i < game.stockPile.cards.length; i += Store.dealCount) {
            const stockPileCard = game.stockPile.cards[i];
            if (lastCard.includes(stockPileCard._id)) continue;

            const validFoundation = game.foundations.find((foundation) => foundation.canDropCard([stockPileCard]));
            const validColumn = game.columns.find((column) => column.canDropCards([stockPileCard]));
            if (validFoundation || validColumn) {
                return [
                    new HintItem(
                        [...game.stockPile.cards, ...game.wastePile.cards],
                        game.wastePile,
                        HintType.MOVE_STOCKPILE,
                        7
                    ),
                ];
            }
        }

        // found card in waste pile
        const wastePileCardsAfterRecycle = [...game.wastePile.cards, ...game.stockPile.cards];
        for (let i = Store.dealCount - 1; i < wastePileCardsAfterRecycle.length; i += Store.dealCount) {
            const stockPileCard = wastePileCardsAfterRecycle[i];
            const validFoundation = game.foundations.find((foundation) => foundation.canDropCard([stockPileCard]));
            const validColumn = game.columns.find((column) => column.canDropCards([stockPileCard]));
            if (validFoundation || validColumn) {
                if (game.stockPile.cards.length === 0) {
                    return [new HintItem([], game.stockPile, HintType.RECYCLE, 7)];
                } else {
                    return [
                        new HintItem(
                            [...game.stockPile.cards, ...game.wastePile.cards],
                            game.wastePile,
                            HintType.MOVE_STOCKPILE,
                            7
                        ),
                    ];
                }
            }
        }
    }

    // check if it is possible to flip the deck and move the card check deals count
    // return [new HintItem([stockPileCard], game.wastePile, HintType.RECYCLE, 7)];

    return [];
}
function checkHint8(game: SolitaireGame, lastCard: string[]) {
    // CHECK 8: Move a card or a column of cards on the tableau to set up a move to the foundation in the next step.
    const availableFoundations = game.foundations.map((c) => c.lastCard);

    for (const foundation of availableFoundations) {
        if (foundation) {
            const validColumn = game.columns.find((c) => c.hasCardToMoveToFoundation(foundation));
            if (validColumn) {
                const { moveGroup, toFoundationCard } = validColumn.buildGroupToRevealCardForFoundation(foundation);

                if (moveGroup.length === 0) continue;

                if (lastCard.includes(moveGroup[0]._id)) continue;

                const validMoveColumn = game.columns.find((c) => c.canDropCards(moveGroup));
                if (validMoveColumn && toFoundationCard) {
                    const validFoundation = game.foundations.find((f) => f.canDropCard([toFoundationCard]));
                    if (validFoundation) {
                        return [new HintItem(moveGroup, validMoveColumn, HintType.MOVE, 8)];
                    }
                }
            }
        }
    }
    return [];
}
